import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wordmark } from '../Landing';
import { ApiError, type AuthTokens, api, setSession } from '../api';
import { PasswordInput } from '../components/ui/Input';
import { type Lang, setLang, t } from '../i18n';

export function StaffLogin() {
  const nav = useNavigate();
  const [household_code, setCode] = useState('');
  const [username, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [needsChange, setNeedsChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const res = await api<{
        tokens: AuthTokens;
        must_change_password?: boolean;
        user: { preferred_lang: Lang };
      }>('/v1/auth/staff/signin', {
        method: 'POST',
        body: JSON.stringify({
          household_code: household_code.toUpperCase(),
          username,
          password,
        }),
      });
      setLang(res.user.preferred_lang);
      if (res.must_change_password) {
        setTokens(res.tokens);
        setNeedsChange(true);
      } else {
        setSession(res.tokens, 'staff');
        nav('/today');
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    if (!tokens) return;
    setBusy(true);
    setErr(null);
    try {
      setSession(tokens, 'staff');
      // Server returns 204 No Content; existing access token stays valid.
      await api('/v1/auth/staff/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: password, new_password: newPassword }),
      });
      nav('/today');
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  if (needsChange) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-10">
        <form
          onSubmit={changePassword}
          className="w-full max-w-md rounded-lg border border-border bg-white p-6 shadow-elev-3 sm:p-8 space-y-4"
        >
          <Wordmark className="mb-6" />
          <p className="text-sm text-muted-foreground text-center">
            {t('Pick a new password before you continue.')}
          </p>
          <input
            className="input"
            type="password"
            placeholder={`${t('New password')} (10+)`}
            aria-label={t('New password')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={10}
            required
          />
          {err && <div className="text-danger text-sm">{err}</div>}
          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {t('Continue')}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-lg border border-border bg-white p-6 shadow-elev-3 sm:p-8 space-y-4"
      >
        <Wordmark className="mb-7" />
        <input
          className="input"
          dir="ltr"
          placeholder={`${t('Household code')} (e.g. APXY-7K)`}
          aria-label={t('Household code')}
          value={household_code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          autoComplete="off"
          required
        />
        <input
          className="input"
          dir="ltr"
          placeholder={t('Username or phone')}
          aria-label={t('Username or phone')}
          value={username}
          onChange={(e) => setUser(e.target.value)}
          autoComplete="username"
          // biome-ignore lint/a11y/noAutofocus: staff sign-in is a dedicated full-screen route and this is its first field; opening ready to type is the intended mobile-first flow
          autoFocus
          required
        />
        <PasswordInput
          placeholder={t('Password')}
          aria-label={t('Password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        {err && <div className="text-danger text-sm">{err}</div>}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? '…' : t('Sign in')}
        </button>
      </form>
    </div>
  );
}
